/**
 * Axios API client with JWT interceptors.
 *
 * Phase 42 audit (F1, F5): pre-fix this file had two issues this audit
 * pass surfaced. F1 — the 401 interceptor stored only the new
 * `access_token` from the /auth/refresh response, discarding the new
 * `refresh_token`. The backend's Phase 21 refresh-token rotation
 * blocklists the consumed token after every refresh, so the second
 * refresh always failed with 401 (the still-stored old refresh_token
 * was now blocklisted) and bumped the user to /login. F5 — 403
 * responses (role downgraded server-side after the SPA loaded) didn't
 * trigger any client-side state refresh, so the SPA showed protected
 * UI for routes the API was actively rejecting. Both fixed below.
 */
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// Request interceptor: attach JWT token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401 (expired token) + 403 (role drift)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // F1: 401 → refresh access AND refresh tokens, retry original request.
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refresh_token");
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, null, {
          headers: { Authorization: `Bearer ${refreshToken}` },
        });

        // Store BOTH tokens — the backend rotated the refresh token and
        // blocklisted the old one (Phase 21 H4). If we only store the
        // new access token and keep the old refresh token, the next
        // refresh will fail because we'll send a blocklisted token.
        const { access_token, refresh_token } = response.data;
        localStorage.setItem("access_token", access_token);
        if (refresh_token) {
          localStorage.setItem("refresh_token", refresh_token);
        }
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/login";
      }
    }

    // F5: 403 → backend says caller's role doesn't grant this action.
    // Trigger a stale-role re-check via the auth store's checkAuth so
    // the SPA's in-memory role and the backend's idea of the role
    // converge. If checkAuth determines the role no longer permits the
    // user's current page, the page guard will redirect them. We
    // import dynamically to avoid a circular module dependency
    // (authStore -> api -> authStore).
    if (status === 403 && !originalRequest._roleCheckTriggered) {
      originalRequest._roleCheckTriggered = true;
      // Fire-and-forget; don't await (we're returning the rejection
      // immediately so the caller sees the 403). The store update
      // happens in the background and triggers a re-render that the
      // ProtectedRoute guard can act on.
      import("../store/authStore").then(({ default: useAuthStore }) => {
        const { checkAuth } = useAuthStore.getState();
        checkAuth?.();
      }).catch(() => { /* swallow — best-effort UX nicety */ });
    }

    return Promise.reject(error);
  }
);

export default api;
